import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { atom, useRecoilState, useSetRecoilState } from "recoil";
import PageTitle from "../../components/DefaultStyles/PageTitle";
import {
  PointTabBar,
  PointTabColumn,
  PointTabContainer,
  PointTabText,
} from "../../components/pages/Benefit/Point/PointTab";

import YoutubeMatchingInfo, {
  youtubeInfoFilterState,
} from "./YoutubeMatchingInfo";
import GPTSoloMatchingInfo from "./GPTSoloMatchingInfo";
import { useApi } from "../../hooks/useApi";
import { useDashboardQuota } from "../../hooks/useDashboardQuota";
import { useDashboardBalances, formatDashboardBalance } from "../../hooks/useDashboardBalances";
import { Link } from "react-router-dom";
import GrokSoloMatchingInfo from "./GrokSoloMatchingInfo";
import PerplexitySoloMatchingInfo from "./PerplexitySoloMatchingInfo";

interface IDefItem {
  id: number;
  name: string;
  cnt: string;
  today: string;
}

const youtubeInfoTabState = atom<number>({
  key: "youtubeInfoTabState",
  default: 0,
});

type YoutubeSubTab = 0 | 1 | 2;

const youtubeSubTabState = atom<YoutubeSubTab>({
  key: "youtubeSubTabState",
  default: 0,
});

const YOUTUBE_SUBTAB_SERVSUBSID = [37080, 37000, 37050] as const;

const REFRESH_INTERVAL = 30000;

export default function YoutubeInfo() {
  const { data: quota, error: quotaError, isRefreshing: isQuotaRefreshing, refresh: refreshQuota } = useDashboardQuota();
  const { balances, isRefreshing, refresh: refreshBalances } = useDashboardBalances();
  const [tab, setTab] = useRecoilState(youtubeInfoTabState);
  const [youtubeSubTab, setYoutubeSubTab] = useRecoilState(youtubeSubTabState);
  const setYoutubeFilter = useSetRecoilState(youtubeInfoFilterState);

  const handleYoutubeSubTabClick = (nextSubTab: YoutubeSubTab) => {
    if (nextSubTab !== youtubeSubTab) {
      setYoutubeFilter((prev) => ({ ...prev, page: 0 }));
    }

    setYoutubeSubTab(nextSubTab);
  };

  const {
    response: defResponse,
    loading: defLoading,
    fetchData: fetchDefData,
  } = useApi({
    method: "get",
    url: "version/systemdef",
  });

  const fetchDefDataRef = useRef(fetchDefData);
  fetchDefDataRef.current = fetchDefData;

  useEffect(() => {
    const id = setInterval(() => {
      fetchDefDataRef.current({
        method: "get",
        url: "version/systemdef",
      });
    }, REFRESH_INTERVAL);

    return () => clearInterval(id);
  }, []);

  const defData: IDefItem[] | undefined = defResponse?.data;
  const visibleDefData = defData?.filter((item) => !/gpt/i.test(item.name));

  return (
    <>
      <PageTitle title="개인 연간 판매 파티 관리" />

      <OverviewSections>
        <OverviewSection aria-labelledby="today-status-title">
          <OverviewHeader>
            <StatusCardTitle id="today-status-title" style={{ marginBottom: 0 }}>
              오늘 현황
            </StatusCardTitle>
            <Link to="/linkid-manage/status">설정으로 이동 &gt;</Link>
          </OverviewHeader>
          {visibleDefData && visibleDefData.length > 0 && (
            <StatusBoard>
              {visibleDefData.map((d) => (
                <StatusCard key={d.id}>
                  <StatusCardTitle>{d.name}</StatusCardTitle>
                  <StatusRow>
                    <StatusItem>
                      <StatusLabel>오늘</StatusLabel>
                      <StatusValue>{d.today}</StatusValue>
                    </StatusItem>
                    <StatusItem>
                      <StatusLabel>일일 제한</StatusLabel>
                      <StatusValue>{d.cnt}</StatusValue>
                    </StatusItem>
                  </StatusRow>
                </StatusCard>
              ))}
            </StatusBoard>
          )}
          <RefreshInfo>30초마다 자동 갱신됩니다</RefreshInfo>
        </OverviewSection>
        <OverviewSection aria-labelledby="dashboard-title">
          <OverviewHeader>
            <StatusCardTitle id="dashboard-title" style={{ marginBottom: 0 }}>
              Dashboard
            </StatusCardTitle>
          </OverviewHeader>
          <DashboardBoard>
            <div>
              <DashboardSection aria-labelledby="balance-title">
                <BalanceHeader>
                  <StatusCardTitle id="balance-title" style={{ marginBottom: 0 }}>
                    Balance
                  </StatusCardTitle>
                  <BalanceRefreshButton
                    type="button"
                    onClick={refreshBalances}
                    disabled={isRefreshing}
                    aria-label={isRefreshing ? "잔액 조회 중" : "잔액 새로고침"}
                    aria-busy={isRefreshing}
                    title={isRefreshing ? "잔액 조회 중" : "잔액 새로고침"}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 7v5h-5" />
                      <path d="M19.1 8a8 8 0 1 0 .4 7" />
                    </svg>
                  </BalanceRefreshButton>
                </BalanceHeader>
                <DashboardMetrics>
                  {[
                    { name: "Grizzly", value: formatDashboardBalance(balances.grizzly), amount: balances.grizzly.amount },
                    { name: "Online Sim", value: formatDashboardBalance(balances.onlinesim), amount: balances.onlinesim.amount },
                    // { name: "Decodo", value: "$0", amount: 0 },
                  ].map((service) => (
                    <StatusItem key={service.name}>
                      <StatusLabel>{service.name}</StatusLabel>
                      <StatusValue $lowBalance={service.amount !== null && service.amount < 5}>
                        {service.value}
                      </StatusValue>
                    </StatusItem>
                  ))}
                </DashboardMetrics>
              </DashboardSection>
              <RefreshInfo style={{ marginTop: 16, marginBottom: 0 }}>
                5분마다 자동갱신됩니다.
              </RefreshInfo>
            </div>
            <div>
              <DashboardSection aria-labelledby="quota-title">
                <BalanceHeader>
                  <QuotaHeader>
                    <StatusCardTitle id="quota-title" style={{ marginBottom: 0 }}>Quota</StatusCardTitle>
                    <QuotaDescription>합계 (신규/연장)</QuotaDescription>
                  </QuotaHeader>
                  <BalanceRefreshButton
                    type="button"
                    onClick={refreshQuota}
                    disabled={isQuotaRefreshing}
                    aria-label={isQuotaRefreshing ? "할당 건수 조회 중" : "할당 건수 새로고침"}
                    aria-busy={isQuotaRefreshing}
                    title={isQuotaRefreshing ? "할당 건수 조회 중" : "할당 건수 새로고침"}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 7v5h-5" />
                      <path d="M19.1 8a8 8 0 1 0 .4 7" />
                    </svg>
                  </BalanceRefreshButton>
                </BalanceHeader>
                <DashboardMetrics>
                  {([
                    { label: "today", key: "today" },
                    { label: "d+1", key: "tomorrow" },
                    { label: "d+2", key: "dayAfterTomorrow" },
                  ] as const).map((day) => (
                    <StatusItem key={day.key}>
                      <StatusLabel>{day.label}</StatusLabel>
                      <QuotaCounts>
                        <QuotaValue>
                          {quota ? `${(quota[day.key].new + quota[day.key].renewal).toLocaleString()}개` : "—"}
                        </QuotaValue>
                        {quota && (
                          <QuotaBreakdown>
                            ({quota[day.key].new.toLocaleString()}/{quota[day.key].renewal.toLocaleString()})
                          </QuotaBreakdown>
                        )}
                      </QuotaCounts>
                    </StatusItem>
                  ))}
                </DashboardMetrics>
                {quotaError && <QuotaError role="status">할당 건수를 불러오지 못했습니다.</QuotaError>}
              </DashboardSection>
              <RefreshInfo style={{ marginTop: 16, marginBottom: 0 }}>
                5분마다 자동갱신됩니다.
              </RefreshInfo>
            </div>
          </DashboardBoard>
        </OverviewSection>
      </OverviewSections>

      <PointTabContainer>
        <PointTabColumn onClick={() => setTab(0)}>
          <PointTabText isActive={tab === 0}>유튜브 개인</PointTabText>
          <YoutubeTabBar tab={tab} />
        </PointTabColumn>

        {/* <PointTabColumn onClick={() => setTab(1)}>
          <PointTabText isActive={tab === 1}>GPT 개인</PointTabText>
        </PointTabColumn> */}

        {/* <PointTabColumn onClick={() => setTab(2)}>
          <PointTabText isActive={tab === 2}>Grok 개인</PointTabText>
        </PointTabColumn> */}
        <PointTabColumn onClick={() => setTab(2)}>
          <PointTabText isActive={tab === 2}>Perplexity 개인</PointTabText>
        </PointTabColumn>
      </PointTabContainer>

      {tab === 0 && (
        <SubTabContainer>
          <SubTabButton
            type="button"
            isActive={youtubeSubTab === 0}
            onClick={() => handleYoutubeSubTabClick(0)}
          >
            3개월
          </SubTabButton>
          <SubTabButton
            type="button"
            isActive={youtubeSubTab === 1}
            onClick={() => handleYoutubeSubTabClick(1)}
          >
            6개월
          </SubTabButton>
          <SubTabButton
            type="button"
            isActive={youtubeSubTab === 2}
            onClick={() => handleYoutubeSubTabClick(2)}
          >
            12개월
          </SubTabButton>
        </SubTabContainer>
      )}

      {tab === 0 && (
        <YoutubeMatchingInfo
          servsubsidFilter={YOUTUBE_SUBTAB_SERVSUBSID[youtubeSubTab]}
        />
      )}
      {tab === 1 && <GPTSoloMatchingInfo />}
      {tab === 2 && <PerplexitySoloMatchingInfo />}
      {/* {tab === 2 && <GrokSoloMatchingInfo />} */}
    </>
  );
}

const YoutubeTabBar = styled(PointTabBar)`
  width: ${({ tab }) => (tab === 0 ? 80 : tab === 1 ? 80 : 120)}px;
  left: ${({ tab }) => (tab === 0 ? 0 : tab === 1 ? 100 : 100)}px;
`;

const OverviewSections = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  gap: 20px;
  margin-bottom: 12px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const OverviewSection = styled.section`
  min-width: 0;
`;

const OverviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  margin-bottom: 16px;
`;

const DashboardBoard = styled.div`
  display: grid;
  align-items: start;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const DashboardMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  text-align: center;
`;

const BalanceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const BalanceRefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 4px;
  margin: -6px -4px -6px 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #808080;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f0f5fa;
    color: #1565c0;
  }

  &:focus-visible {
    outline: 2px solid #1565c0;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: wait;
  }
`;

const DashboardSection = styled.section`
  min-width: 0;
  min-height: 128px;
  padding: 20px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
`;

const StatusBoard = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const StatusCard = styled.div`
  flex: 1;
  min-width: 200px;
  min-height: 128px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
`;

const StatusCardTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2f2f38;
  margin-bottom: 12px;
`;

const StatusRow = styled.div`
  display: flex;
  gap: 24px;
`;

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const StatusLabel = styled.span`
  font-size: 13px;
  color: #808080;
`;

const StatusValue = styled.span<{ $lowBalance?: boolean }>`
  font-size: 22px;
  font-weight: 700;
  color: ${({ $lowBalance }) => ($lowBalance ? "#d32f2f" : "#1565c0")};
`;

const RefreshInfo = styled.div`
  font-size: 12px;
  color: #9e9e9e;
  text-align: right;
  margin-bottom: 12px;
`;

const SubTabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin: 8px 0 16px;
`;

const SubTabButton = styled.button<{ isActive: boolean }>`
  border: 1px solid ${({ isActive }) => (isActive ? "#4a90d9" : "#d9d9d9")};
  background: ${({ isActive }) => (isActive ? "#e9f4ff" : "#ffffff")};
  color: ${({ isActive }) => (isActive ? "#2563eb" : "#666")};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
`;

const QuotaHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 10px;
`;

const QuotaDescription = styled.span`
  font-size: 12px;
  color: #808080;
`;

const QuotaCounts = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: baseline;
  gap: 4px;
  margin-top: 4px;
`;

const QuotaBreakdown = styled.span`
  font-size: 13px;
  color: #808080;
`;

const QuotaValue = styled.span`
  color: #1565c0;
  font-size: 18px;
  font-weight: 700;
`;

const QuotaError = styled.div`
  margin-top: 12px;
  color: #d32f2f;
  font-size: 12px;
`;
